import React, { useState, useEffect } from 'react'
import { GraduationCap, Calendar, Clock, Users, DollarSign, AlertCircle, CheckCircle, XCircle, ChevronDown } from 'lucide-react'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import Modal from '../../components/common/Modal'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import toast from 'react-hot-toast'
import { inscripcionService } from '../../services/inscripcionService'
import { useForm } from 'react-hook-form'

const Inscripciones = () => {
  const [programas, setProgramas] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedPrograma, setSelectedPrograma] = useState(null)
  const [selectedGrupo, setSelectedGrupo] = useState(null)
  const [conflictos, setConflictos] = useState([])
  const [verificando, setVerificando] = useState(false)
  const [verificacionResultado, setVerificacionResultado] = useState(null)
  const [misInscripciones, setMisInscripciones] = useState([])
  const [programasExpandidos, setProgramasExpandidos] = useState({})

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      numero_cuotas: 3
    }
  })

  const numeroCuotas = watch('numero_cuotas')

  useEffect(() => {
    fetchProgramas()
  }, [misInscripciones])

  const fetchProgramas = async () => {
    try {
      setLoading(true)
      const response = await inscripcionService.getProgramasDisponibles()
      const misInscResponse = await inscripcionService.getMisInscripciones()
      
      if (response.success) {
        setProgramas(response.data || [])
      } else {
        // Si el error es por documentos no aprobados, mostrar mensaje específico
        if (response.message?.includes('documentos aprobados')) {
          toast.error('Debes tener tus documentos aprobados para poder inscribirte. Completa la verificación de documentos primero.')
        } else {
          toast.error(response.message || 'Error al cargar programas')
        }
        setProgramas([])
      }

      if (misInscResponse && misInscResponse.success) {
        setMisInscripciones(Array.isArray(misInscResponse.data.data) ? misInscResponse.data.data : [])
        console.log('InscripcionesResponse:', misInscResponse.data.data)
        console.log("DATA REAL:", misInscResponse.data)
        console.log("DATA.DATA:", misInscResponse.data.data)
        console.log('Inscripciones:', misInscripciones)
      } else {
        setMisInscripciones([])
      }
    } catch (error) {
      // Verificar si el error es por documentos no aprobados
      if (error.response?.data?.message?.includes('documentos aprobados')) {
        toast.error('Debes tener tus documentos aprobados para poder inscribirte. Completa la verificación de documentos primero.')
      } else {
        toast.error('Error de conexión')
      }
      setProgramas([])
      setMisInscripciones([])
    } finally {
      setLoading(false)
    }
  }

  const handleInscribirse = async (programa) => {
    setSelectedPrograma(programa)
    setSelectedGrupo(null)
    setConflictos([])
    setVerificacionResultado(null)
    reset({
      programa_id: programa.id,
      numero_cuotas: 3
    })
    
    // Verificar conflictos de horario para todos los grupos del programa
    setVerificando(true)
    try {
      const verificacion = await inscripcionService.verificarHorariosPrograma(programa.id)
      
      if (verificacion.success) {
        setVerificacionResultado(verificacion)
        if (verificacion.tieneConflictos) {
          toast.error(`Se encontraron conflictos en ${verificacion.total_grupos_con_conflicto} de ${verificacion.total_grupos_programa} grupos`)
        }
        setShowModal(true)
      } else {
        toast.error(verificacion.message || 'Error al verificar horarios')
      }
    } catch (error) {
      console.error('Error verificando horarios:', error)
      toast.error('Error al verificar horarios. Intenta nuevamente.')
    } finally {
      setVerificando(false)
    }
  }

  const onSubmit = async (data) => {
    if (verificacionResultado?.tieneConflictos && verificacionResultado?.total_grupos_disponibles === 0) {
      toast.error('No puede inscribirse. Todos los grupos del programa tienen conflictos de horario')
      return
    }

    try {
      setLoading(true)
      const response = await inscripcionService.crearInscripcion({
        programa_id: selectedPrograma.id,
        numero_cuotas: parseInt(data.numero_cuotas)
      })
      
      if (response.success) {
        // Crear mensaje con resumen de módulos inscritos
        const modulosInfo = selectedPrograma.modulos?.length 
          ? ` (${selectedPrograma.modulos.length} módulos)` 
          : ''
        const gruposInfo = verificacionResultado?.total_grupos_disponibles 
          ? ` - Inscrito en ${verificacionResultado.total_grupos_disponibles} grupo(s)` 
          : ''
        
        toast.success(`✅ Inscripción realizada exitosamente${modulosInfo}${gruposInfo}`)
        setShowModal(false)
        setSelectedPrograma(null)
        setSelectedGrupo(null)
        setConflictos([])
        setVerificacionResultado(null)
        reset()
        await fetchProgramas()
      } else {
        toast.error(response.message || 'Error al realizar inscripción')
        if (response.errors) {
          Object.keys(response.errors).forEach(key => {
            toast.error(`${key}: ${response.errors[key]}`)
          })
        }
      }
    } catch (error) {
      toast.error('Error al realizar inscripción')
    } finally {
      setLoading(false)
    }
  }

  const toggleProgramaExpanded = (programaId) => {
    setProgramasExpandidos(prev => ({
      ...prev,
      [programaId]: !prev[programaId]
    }))
  }

  const formatHorario = (horario) => {
    if (!horario) return '-'
    return `${horario.dias} ${horario.hora_ini} - ${horario.hora_fin}`
  }

  if (loading && programas.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
          <GraduationCap className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold gradient-text">Inscripciones</h1>
          <p className="text-gray-600 dark:text-gray-400">Inscríbete en los programas académicos disponibles</p>
        </div>
      </div>

      {programas.length === 0 ? (
        <Card className="gradient" shadow="glow-lg">
          <div className="text-center py-12">
            <GraduationCap className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {loading ? 'Cargando programas...' : 'No hay programas disponibles'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {loading 
                ? 'Por favor espera...' 
                : 'Actualmente no hay programas con grupos disponibles para inscripción. Verifica que tus documentos estén aprobados.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {programas.map((programa) => {
            const estaInscrito = misInscripciones.some(insc => insc.programa_id === programa.id)
            
            return (
            <Card 
              key={programa.id} 
              className={estaInscrito ? "gradient bg-green-50 dark:bg-green-900/10" : "gradient"} 
              shadow="glow-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <button 
                      onClick={() => toggleProgramaExpanded(programa.id)}
                      className="transition-transform duration-200"
                      style={{ transform: programasExpandidos[programa.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <ChevronDown className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </button>
                    <h3 className="text-2xl font-bold gradient-text">{programa.nombre}</h3>
                    {estaInscrito && (
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        ✅ Inscrito
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span>Costo: {parseFloat(programa.costo || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Duración: {programa.duracion_meses || 0} meses</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{programa.total_grupos} grupo(s) disponible(s)</span>
                    </div>
                  </div>
                  {programa.institucion && (
                    <p className="text-sm text-gray-500 mt-2">Institución: {programa.institucion}</p>
                  )}
                </div>
              </div>

             

              {/* Sección de Grupos (Expandible) */}
              {estaInscrito && programa.grupos_disponibles && programa.grupos_disponibles.length > 0 && programasExpandidos[programa.id] && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Grupos Inscritos:</h4>
                  {programa.grupos_disponibles.map((grupo) => (
                    <div
                      key={grupo.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                              {`${grupo.modulo}-${grupo.nombre}` || 'Grupo'}
                            </h5>
                            {grupo.cupos_disponibles > 0 && (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                {grupo.cupos_disponibles} cupos disponibles
                              </span>
                            )}
                          </div>
                          {grupo.docente && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              Docente: {grupo.docente}
                            </p>
                          )}
                          <div className="space-y-1">
                            {grupo.horarios && grupo.horarios.length > 0 ? (
                              grupo.horarios.map((horario, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <Clock className="h-4 w-4" />
                                  <span>{formatHorario(horario)}</span>
                                  {horario.aula && <span className="text-xs">({horario.aula})</span>}
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500">Sin horarios asignados</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(grupo.fecha_ini).toLocaleDateString()} - {new Date(grupo.fecha_fin).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )
          })}
        </div>
      )}

      {/* Modal de Inscripción */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setSelectedPrograma(null)
          setSelectedGrupo(null)
          setConflictos([])
          setVerificacionResultado(null)
          reset()
        }}
        title="Confirmar Inscripción"
        size="lg"
      >
        {selectedPrograma && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {selectedPrograma.nombre}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Duración: {selectedPrograma.duracion_meses} meses
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Costo: {parseFloat(selectedPrograma.costo || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
              </p>

              {/* Módulos/Materias del Programa */}
              {selectedPrograma.modulos && selectedPrograma.modulos.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    📚 Módulos/Materias a Inscribirse:
                  </p>
                  <div className="space-y-2">
                    {selectedPrograma.modulos.map((modulo, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {modulo.nombre || modulo}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Total: {selectedPrograma.modulos.length} módulo(s)
                  </p>
                </div>
              )}
            </div>

            {verificacionResultado && (
              <>
                {/* Resumen de conflictos */}
                <div className={`p-4 rounded-lg border-2 ${
                  verificacionResultado.tieneConflictos 
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800' 
                    : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800'
                }`}>
                  <div className="flex items-start gap-3 mb-4">
                    {verificacionResultado.tieneConflictos ? (
                      <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h5 className={`font-bold text-lg mb-2 ${
                        verificacionResultado.tieneConflictos 
                          ? 'text-yellow-900 dark:text-yellow-100' 
                          : 'text-green-900 dark:text-green-100'
                      }`}>
                        {verificacionResultado.tieneConflictos 
                          ? '⚠️ Conflictos Detectados' 
                          : '✅ Sin Conflictos'}
                      </h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className={`text-xs font-semibold ${
                            verificacionResultado.tieneConflictos 
                              ? 'text-yellow-700 dark:text-yellow-300' 
                              : 'text-green-700 dark:text-green-300'
                          }`}>
                            Total de Grupos
                          </p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {verificacionResultado.total_grupos_programa}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                            Con Conflictos
                          </p>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {verificacionResultado.total_grupos_con_conflicto}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-green-700 dark:text-green-300">
                            Disponibles
                          </p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {verificacionResultado.total_grupos_disponibles}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grupos con conflictos */}
                {verificacionResultado.grupos_con_conflictos && verificacionResultado.grupos_con_conflictos.length > 0 && (
                  <div className="space-y-3">
                    <h6 className="font-semibold text-red-900 dark:text-red-100 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Grupos con Conflictos de Horario ({verificacionResultado.total_grupos_con_conflicto})
                    </h6>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {verificacionResultado.grupos_con_conflictos.map((grupo, idx) => (
                        <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {grupo.modulo} - {grupo.nombre}
                              </p>
                              {grupo.docente && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  👨‍🏫 {grupo.docente}
                                </p>
                              )}
                              {grupo.horarios && grupo.horarios.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {grupo.horarios.map((h, hidx) => (
                                    <p key={hidx} className="text-xs text-gray-700 dark:text-gray-300">
                                      📅 {h.dias || 'Sin día'} {h.hora_ini} - {h.hora_fin}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {grupo.razon_conflicto && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-2 italic">
                                  {grupo.razon_conflicto}
                                </p>
                              )}
                            </div>
                            <span className="text-xl">❌</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grupos disponibles */}
                {verificacionResultado.grupos_disponibles && verificacionResultado.grupos_disponibles.length > 0 && (
                  <div className="space-y-3">
                    <h6 className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Grupos Disponibles ({verificacionResultado.total_grupos_disponibles})
                    </h6>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {verificacionResultado.grupos_disponibles.map((grupo, idx) => (
                        <div key={idx} className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {grupo.modulo} - {grupo.nombre}
                              </p>
                              {grupo.docente && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  👨‍🏫 {grupo.docente}
                                </p>
                              )}
                              {grupo.horarios && grupo.horarios.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {grupo.horarios.map((h, hidx) => (
                                    <p key={hidx} className="text-xs text-gray-700 dark:text-gray-300">
                                      📅 {h.dias || 'Sin día'} {h.hora_ini} - {h.hora_fin}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {/* Desglose de cuotas por grupo */}
                              {numeroCuotas && (grupo.costo || selectedPrograma.costo) && (
                                <div className="mt-3 p-2 bg-white dark:bg-gray-800 rounded border border-green-300 dark:border-green-700">
                                  <p className="text-xs font-semibold text-green-900 dark:text-green-100 mb-2">
                                    💰 Desglose de Cuotas:
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Costo del grupo: <span className="font-bold text-gray-900 dark:text-gray-100">
                                      {parseFloat(grupo.costo || selectedPrograma.costo || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                                    </span>
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Cuotas: <span className="font-bold text-gray-900 dark:text-gray-100">{numeroCuotas}</span>
                                  </p>
                                  <p className="text-sm font-bold text-green-700 dark:text-green-300 mt-2">
                                    Monto por cuota: {parseFloat(((grupo.costo || selectedPrograma.costo) / numeroCuotas) || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                                  </p>
                                </div>
                              )}
                            </div>
                            <span className="text-xl">✅</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensaje informativo */}
                {verificacionResultado.tieneConflictos && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      💡 <span className="font-semibold">Nota:</span> {verificacionResultado.total_grupos_disponibles > 0 
                        ? `Puedes inscribirse en los ${verificacionResultado.total_grupos_disponibles} grupo(s) sin conflicto. Los demás grupos no estarán disponibles.`
                        : 'No hay grupos disponibles sin conflictos. Por favor, revisa tu horario.'}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Campo de cuotas */}
            {verificacionResultado && verificacionResultado.total_grupos_disponibles > 0 && (
              <Input
                label="Número de Cuotas *"
                type="number"
                min="1"
                max="12"
                placeholder="3"
                error={errors.numero_cuotas?.message}
                {...register('numero_cuotas', { 
                  required: 'El número de cuotas es obligatorio',
                  min: { value: 1, message: 'Mínimo 1 cuota' },
                  max: { value: 12, message: 'Máximo 12 cuotas' }
                })}
              />
            )}

            {/* Botones de acción */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false)
                  setSelectedPrograma(null)
                  setSelectedGrupo(null)
                  setConflictos([])
                  setVerificacionResultado(null)
                  reset()
                }}
              >
                Cerrar
              </Button>
              {verificacionResultado && verificacionResultado.total_grupos_disponibles > 0 && (
                <Button
                  type="submit"
                  variant="primary"
                  icon={<CheckCircle className="h-5 w-5" />}
                  disabled={loading || verificando}
                >
                  {loading ? 'Procesando...' : 'Confirmar Inscripción'}
                </Button>
              )}
            </div>

            {verificacionResultado && verificacionResultado.total_grupos_disponibles === 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 rounded-lg text-center">
                <p className="text-red-900 dark:text-red-100 font-semibold">
                  ❌ No se puede inscribirse. Todos los grupos tienen conflicto de horario.
                </p>
              </div>
            )}
          </form>
        )}
      </Modal>
    </div>
  )
}

export default Inscripciones

